import React, { createContext, useContext, useState, useEffect } from 'react';
import { productAPI } from '../services/api';

const InventoryContext = createContext();

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

const mapProductToInventoryItem = (p, index) => {
  const currentStock = p.stockQuantity != null ? p.stockQuantity : 0;
  const minStock = p.minStockQuantity != null ? p.minStockQuantity : 0;
  const status = currentStock === 0 ? 'out-of-stock' : currentStock <= minStock ? 'low-stock' : 'in-stock';
  return {
    id: p.id || index + 1,
    productId: p.id,
    productName: p.name || p.productName,
    sku: p.sku || '',
    currentStock,
    minStock,
    maxStock: 100,
    reservedStock: 0,
    availableStock: currentStock,
    unit: 'adet',
    location: '',
    lastUpdated: p.updatedAt || new Date().toISOString(),
    status,
    supplier: '',
    cost: 0,
    sellingPrice: p.price != null ? Number(p.price) : 0,
    category: p.category || 'other'
  };
};

export const InventoryProvider = ({ children }) => {
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const loadInventory = async () => {
    setIsLoading(true);
    try {
      const response = await productAPI.getActiveProducts();
      const products = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const mapped = (products || []).map((p, i) => mapProductToInventoryItem(p, i));
      setInventory(mapped);
    } catch (err) {
      console.error('Error loading inventory from API:', err);
      setInventory([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, []);

  // Generate alerts based on stock levels
  useEffect(() => {
    const newAlerts = inventory
      .filter(item => item.currentStock <= item.minStock)
      .map(item => ({
        id: `alert-${item.id}`,
        type: item.currentStock === 0 ? 'critical' : 'warning',
        message: `${item.productName} stok seviyesi düşük (${item.currentStock} ${item.unit})`,
        productId: item.productId,
        productName: item.productName,
        currentStock: item.currentStock,
        minStock: item.minStock,
        timestamp: new Date().toISOString()
      }));
    
    setAlerts(newAlerts);
  }, [inventory]);

  const updateStock = async (productId, quantity, operation = 'adjust') => {
    setIsLoading(true);
    try {
      if (operation === 'add' || operation === 'adjust') {
        await productAPI.increaseStock(productId, quantity);
      } else if (operation === 'subtract') {
        await productAPI.decreaseStock(productId, quantity);
      } else if (operation === 'set') {
        const item = inventory.find(i => i.productId === productId);
        if (item != null) {
          const diff = quantity - item.currentStock;
          if (diff > 0) await productAPI.increaseStock(productId, diff);
          else if (diff < 0) await productAPI.decreaseStock(productId, -diff);
        }
      }
      await loadInventory();
      return { success: true, message: 'Stok güncellendi' };
    } catch (err) {
      console.error('Stock update error:', err);
      return { success: false, message: err.response?.data?.message || err.response?.data?.error || 'Stok güncellenirken hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  const reserveStock = async (productId, quantity) => {
    setIsLoading(true);
    try {
      setInventory(prev => prev.map(item => {
        if (item.productId === productId) {
          const newReservedStock = item.reservedStock + quantity;
          const newAvailableStock = Math.max(0, item.currentStock - newReservedStock);
          
          return {
            ...item,
            reservedStock: newReservedStock,
            availableStock: newAvailableStock
          };
        }
        return item;
      }));
      
      return { success: true, message: 'Stok rezerve edildi' };
    } catch (error) {
      console.error('Stock reservation error:', error);
      return { success: false, message: 'Stok rezerve edilirken hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  const releaseReservedStock = async (productId, quantity) => {
    setIsLoading(true);
    try {
      setInventory(prev => prev.map(item => {
        if (item.productId === productId) {
          const newReservedStock = Math.max(0, item.reservedStock - quantity);
          const newAvailableStock = item.currentStock - newReservedStock;
          
          return {
            ...item,
            reservedStock: newReservedStock,
            availableStock: newAvailableStock
          };
        }
        return item;
      }));
      
      return { success: true, message: 'Rezerve stok serbest bırakıldı' };
    } catch (error) {
      console.error('Stock release error:', error);
      return { success: false, message: 'Rezerve stok serbest bırakılırken hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  const addNewProduct = async (productData) => {
    setIsLoading(true);
    try {
      await productAPI.createProduct({
        name: productData.productName,
        sku: productData.sku,
        stockQuantity: productData.currentStock || 0,
        minStockQuantity: productData.minStock || 0,
        price: productData.sellingPrice || 0,
        category: productData.category || 'other',
        isActive: true,
        inStock: (productData.currentStock || 0) > 0
      });
      await loadInventory();
      return { success: true, message: 'Yeni ürün eklendi' };
    } catch (error) {
      console.error('Add product error:', error);
      return { success: false, message: error.response?.data?.error || 'Ürün eklenirken hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  const getInventoryStats = () => {
    const totalProducts = inventory.length;
    const inStockProducts = inventory.filter(item => item.status === 'in-stock').length;
    const lowStockProducts = inventory.filter(item => item.status === 'low-stock').length;
    const outOfStockProducts = inventory.filter(item => item.status === 'out-of-stock').length;
    
    const totalValue = inventory.reduce((sum, item) => sum + (item.currentStock * item.cost), 0);
    const totalReservedValue = inventory.reduce((sum, item) => sum + (item.reservedStock * item.cost), 0);
    
    return {
      totalProducts,
      inStockProducts,
      lowStockProducts,
      outOfStockProducts,
      totalValue,
      totalReservedValue,
      totalStock: inventory.reduce((sum, item) => sum + item.currentStock, 0),
      totalReserved: inventory.reduce((sum, item) => sum + item.reservedStock, 0)
    };
  };

  const getLowStockItems = () => {
    return inventory.filter(item => item.currentStock <= item.minStock);
  };

  const getOutOfStockItems = () => {
    return inventory.filter(item => item.currentStock === 0);
  };

  const getInventoryByCategory = () => {
    const categories = {};
    inventory.forEach(item => {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
    });
    return categories;
  };

  const searchInventory = (query) => {
    const searchTerm = query.toLowerCase();
    return inventory.filter(item => 
      item.productName.toLowerCase().includes(searchTerm) ||
      item.sku.toLowerCase().includes(searchTerm) ||
      item.location.toLowerCase().includes(searchTerm) ||
      item.supplier.toLowerCase().includes(searchTerm)
    );
  };

  const clearAlert = (alertId) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const clearAllAlerts = () => {
    setAlerts([]);
  };

  const value = {
    inventory,
    isLoading,
    alerts,
    loadInventory,
    updateStock,
    reserveStock,
    releaseReservedStock,
    addNewProduct,
    getInventoryStats,
    getLowStockItems,
    getOutOfStockItems,
    getInventoryByCategory,
    searchInventory,
    clearAlert,
    clearAllAlerts
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};
